import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  Badge,
  Phone,
} from '@mui/icons-material';

interface CrearUsuarioWordPressProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
  tipo_documento: string;
  numero_dni: string;
  numero_carnet_extranjeria: string;
  first_name: string;
  last_name: string;
  telefono: string;
}

interface FormErrors {
  [key: string]: string;
}

const CrearUsuarioWordPress: React.FC<CrearUsuarioWordPressProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
}) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    tipo_documento: '',
    numero_dni: '',
    numero_carnet_extranjeria: '',
    first_name: '',
    last_name: '',
    telefono: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validaciones
  const validateEmail = (email: string): string => {
    if (!email) return 'El email es requerido';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Formato de email inválido';
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'La contraseña es requerida';
    if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
    if (!/(?=.*[a-z])/.test(password)) return 'Debe contener al menos una letra minúscula';
    if (!/(?=.*[A-Z])/.test(password)) return 'Debe contener al menos una letra mayúscula';
    if (!/(?=.*\d)/.test(password)) return 'Debe contener al menos un número';
    return '';
  };

  const validateDNI = (dni: string): string => {
    if (!dni) return 'El número de DNI es requerido';
    if (!/^\d{8}$/.test(dni)) return 'El DNI debe tener exactamente 8 dígitos';
    return '';
  };

  const validateCarnetExtranjeria = (carnet: string): string => {
    if (!carnet) return 'El número de carnet es requerido';
    if (!/^[A-Z0-9]{6,12}$/.test(carnet.toUpperCase())) {
      return 'El carnet debe tener entre 6 y 12 caracteres alfanuméricos';
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    // Password
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    // Confirm Password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    // Role
    if (!formData.role) newErrors.role = 'El rol es requerido';

    // Tipo documento
    if (!formData.tipo_documento) newErrors.tipo_documento = 'El tipo de documento es requerido';

    // Número de documento según tipo
    if (formData.tipo_documento === 'dni') {
      const dniError = validateDNI(formData.numero_dni);
      if (dniError) newErrors.numero_dni = dniError;
    } else if (formData.tipo_documento === 'carnet_extranjeria') {
      const carnetError = validateCarnetExtranjeria(formData.numero_carnet_extranjeria);
      if (carnetError) newErrors.numero_carnet_extranjeria = carnetError;
    }

    // Nombres (opcional pero si se proporciona, validar)
    if (formData.first_name && formData.first_name.length < 2) {
      newErrors.first_name = 'El nombre debe tener al menos 2 caracteres';
    }

    if (formData.last_name && formData.last_name.length < 2) {
      newErrors.last_name = 'El apellido debe tener al menos 2 caracteres';
    }

    // Teléfono (opcional)
    if (formData.telefono && !/^\d{9}$/.test(formData.telefono)) {
      newErrors.telefono = 'El teléfono debe tener 9 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload: any = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
        tipo_documento: formData.tipo_documento,
        first_name: formData.first_name || '',
        last_name: formData.last_name || '',
      };

      // Agregar número de documento según el tipo
      if (formData.tipo_documento === 'dni') {
        payload.numero_dni = formData.numero_dni;
      } else if (formData.tipo_documento === 'carnet_extranjeria') {
        payload.numero_carnet_extranjeria = formData.numero_carnet_extranjeria;
      }

      const response = await fetch('/api/wordpress/community-users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        onSuccess(`Usuario creado exitosamente: ${data.data.email}`);
        handleClose();
      } else {
        onError(data.message || 'Error creando usuario');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      onError('Error de conexión al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      role: '',
      tipo_documento: '',
      numero_dni: '',
      numero_carnet_extranjeria: '',
      first_name: '',
      last_name: '',
      telefono: '',
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    // Asegurar que tenga al menos una mayúscula, minúscula y número
    password += 'A'; // Mayúscula
    password += 'a'; // Minúscula
    password += '1'; // Número
    
    for (let i = 3; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    // Mezclar caracteres
    password = password.split('').sort(() => 0.5 - Math.random()).join('');
    
    setFormData(prev => ({ 
      ...prev, 
      password, 
      confirmPassword: password 
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Person color="primary" />
          <Typography variant="h6">Crear Usuario WordPress</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Información básica */}
          <Typography variant="h6" color="primary" gutterBottom>
            Información Básica
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth error={!!errors.role}>
              <InputLabel>Rol *</InputLabel>
              <Select
                value={formData.role}
                label="Rol *"
                onChange={handleInputChange('role')}
              >
                <MenuItem value="comunidad_estandar">Comunidad Estándar</MenuItem>
                <MenuItem value="comunidad_premium">Comunidad Premium</MenuItem>
              </Select>
              {errors.role && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.role}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Contraseña */}
          <Typography variant="h6" color="primary" gutterBottom>
            Contraseña
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Contraseña *"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!errors.password}
              helperText={errors.password || 'Mínimo 8 caracteres, mayúscula, minúscula y número'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmar Contraseña *"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={generatePassword}
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          >
            Generar Contraseña Segura
          </Button>

          {/* Documento de identidad */}
          <Typography variant="h6" color="primary" gutterBottom>
            Documento de Identidad
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth error={!!errors.tipo_documento}>
              <InputLabel>Tipo de Documento *</InputLabel>
              <Select
                value={formData.tipo_documento}
                label="Tipo de Documento *"
                onChange={handleInputChange('tipo_documento')}
              >
                <MenuItem value="dni">DNI</MenuItem>
                <MenuItem value="carnet_extranjeria">Carnet de Extranjería</MenuItem>
              </Select>
              {errors.tipo_documento && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.tipo_documento}
                </Typography>
              )}
            </FormControl>

            <Box>
              {formData.tipo_documento === 'dni' && (
                <TextField
                  fullWidth
                  label="Número de DNI *"
                  value={formData.numero_dni}
                  onChange={handleInputChange('numero_dni')}
                  error={!!errors.numero_dni}
                  helperText={errors.numero_dni || '8 dígitos'}
                  inputProps={{ maxLength: 8 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {formData.tipo_documento === 'carnet_extranjeria' && (
                <TextField
                  fullWidth
                  label="Número de Carnet *"
                  value={formData.numero_carnet_extranjeria}
                  onChange={handleInputChange('numero_carnet_extranjeria')}
                  error={!!errors.numero_carnet_extranjeria}
                  helperText={errors.numero_carnet_extranjeria || '6-12 caracteres alfanuméricos'}
                  inputProps={{ maxLength: 12 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Información personal (opcional) */}
          <Typography variant="h6" color="primary" gutterBottom>
            Información Personal (Opcional)
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Nombres"
              value={formData.first_name}
              onChange={handleInputChange('first_name')}
              error={!!errors.first_name}
              helperText={errors.first_name}
            />

            <TextField
              fullWidth
              label="Apellidos"
              value={formData.last_name}
              onChange={handleInputChange('last_name')}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />

            <TextField
              fullWidth
              label="Teléfono"
              value={formData.telefono}
              onChange={handleInputChange('telefono')}
              error={!!errors.telefono}
              helperText={errors.telefono || '9 dígitos'}
              inputProps={{ maxLength: 9 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Información importante */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Importante:</strong> El usuario será creado en WordPress con el rol seleccionado. 
            Asegúrate de que el email no esté ya registrado y que el documento sea válido.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Person />}
        >
          {loading ? 'Creando...' : 'Crear Usuario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrearUsuarioWordPress;
